import { useState } from "react";
import { Key, Plus } from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { CreateTokenDialog } from "./create-token-dialog";

function relativeTime(date: Date | string | null): string {
  if (!date) return "Never";
  const d = typeof date === "string" ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

function expiryStatus(
  expiresAt: Date | string | null,
  revokedAt: Date | string | null,
): { label: string; isDestructive: boolean } {
  if (revokedAt) return { label: "Revoked", isDestructive: true };
  if (!expiresAt) return { label: "No expiry", isDestructive: false };
  const d = typeof expiresAt === "string" ? new Date(expiresAt) : expiresAt;
  if (d < new Date()) return { label: "Expired", isDestructive: true };
  const daysLeft = Math.ceil(
    (d.getTime() - Date.now()) / (24 * 60 * 60 * 1000),
  );
  return { label: `${daysLeft}d left`, isDestructive: false };
}

export function AccessTokensTab() {
  const utils = trpc.useUtils();
  const { data: tokens, isLoading } = trpc.accessToken.list.useQuery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const revokeMutation = trpc.accessToken.revoke.useMutation({
    onSuccess: () => {
      toast.success("Token revoked");
      utils.accessToken.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to revoke access token"),
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeTokens = tokens?.filter((t) => !t.revokedAt) ?? [];
  const revokedTokens = tokens?.filter((t) => t.revokedAt) ?? [];
  const hasTokens = Boolean(tokens?.length);

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          {hasTokens && (
            <div className="flex items-center justify-end mb-4">
              <Button size="sm" onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create token
              </Button>
            </div>
          )}
          {!hasTokens ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <Key className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">No access tokens yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Create a token to connect the Iamu MCP server to Claude Code,
                  Cursor, or other AI tools.
                </p>
              </div>
              <Button size="sm" onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create your first token
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[100px] text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeTokens.map((token) => {
                  const status = expiryStatus(token.expiresAt, token.revokedAt);
                  return (
                    <TableRow key={token.id}>
                      <TableCell className="font-medium text-sm">
                        {token.name}
                      </TableCell>
                      <TableCell>
                        <code className="font-mono text-xs text-muted-foreground">
                          {token.keyPrefix}...
                        </code>
                      </TableCell>
                      <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
                        {relativeTime(token.lastUsedAt)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "font-mono text-xs tabular-nums",
                          status.isDestructive
                            ? "text-destructive"
                            : "text-muted-foreground",
                        )}
                      >
                        {status.label}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setTokenToRevoke({
                              id: token.id,
                              name: token.name,
                            })
                          }
                          disabled={
                            revokeMutation.isPending &&
                            revokeMutation.variables?.id === token.id
                          }
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                          aria-label={`Revoke token ${token.name}`}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {revokedTokens.map((token) => (
                  <TableRow key={token.id} className="opacity-50">
                    <TableCell className="text-sm line-through">
                      {token.name}
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">
                        {token.keyPrefix}...
                      </code>
                    </TableCell>
                    <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
                      {relativeTime(token.lastUsedAt)}
                    </TableCell>
                    <TableCell className="font-mono text-xs tabular-nums text-destructive">
                      Revoked
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      —
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateTokenDialog open={dialogOpen} onOpenChange={setDialogOpen} />

      <AlertDialog
        open={!!tokenToRevoke}
        onOpenChange={(open) => !open && setTokenToRevoke(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access token?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke &ldquo;{tokenToRevoke?.name}
              &rdquo;? Any MCP clients, scripts, or integrations using this
              token will immediately lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (tokenToRevoke) {
                  revokeMutation.mutate({ id: tokenToRevoke.id });
                  setTokenToRevoke(null);
                }
              }}
            >
              Revoke token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
